import React from 'react';
import { ChordVoicing } from '../utils/guitarChords';

interface FretboardProps {
  voicing: ChordVoicing;
}

const Fretboard: React.FC<FretboardProps> = ({ voicing }) => {
  const { frets, fingers, baseFret, barres } = voicing;
  
  const numStrings = 6;
  const numFrets = 5; 
  const width = 180;
  const height = 175; 
  const paddingX = 35;
  const paddingY = 20; 
  const stringSpacing = (width - paddingX * 2) / (numStrings - 1);
  const fretSpacing = (height - paddingY - 25) / numFrets; 

  const getFretY = (fretNumber: number) => {
    if (fretNumber === 0) return paddingY - 10;
    const relativeFret = fretNumber - baseFret + 1;
    return paddingY + (relativeFret * fretSpacing) - (fretSpacing / 2);
  };

  const isMuted = (stringIndex: number) => frets[stringIndex] === -1;
  const isOpen = (stringIndex: number) => frets[stringIndex] === 0;
  const stringLabels = ['E', 'A', 'D', 'G', 'B', 'E'];

  return (
    <div className="flex flex-col items-center bg-slate-900 px-4 pt-3 pb-2 rounded-xl shadow-xl border border-slate-700 hover:border-emerald-500/50 hover:shadow-emerald-500/10 transition-all duration-300 group w-full max-w-[200px]">
      <h3 className="text-sm font-bold text-emerald-400 mb-1 text-center truncate w-full">
        {voicing.name}
      </h3>
      
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="drop-shadow-md">
        {/* Frets (Horizontal Lines) */}
        {Array.from({ length: numFrets + 1 }).map((_, i) => (
          <line
            key={`fret-${i}`}
            x1={paddingX}
            y1={paddingY + i * fretSpacing}
            x2={width - paddingX}
            y2={paddingY + i * fretSpacing}
            stroke={i === 0 && baseFret === 1 ? "#cbd5e1" : "#475569"} 
            strokeWidth={i === 0 && baseFret === 1 ? 4 : 1.5}
          />
        ))}

        {/* Strings (Vertical Lines) */}
        {Array.from({ length: numStrings }).map((_, i) => (
          <line
            key={`string-${i}`}
            x1={paddingX + i * stringSpacing}
            y1={paddingY}
            x2={paddingX + i * stringSpacing}
            y2={paddingY + numFrets * fretSpacing + 3}
            stroke="#64748b"
            strokeWidth={1 + (5 - i) * 0.3}
          />
        ))}

        {/* String Labels (Aligned with strings) */}
        {stringLabels.map((label, i) => (
          <text
            key={`label-${i}`}
            x={paddingX + i * stringSpacing}
            y={height - 4}
            textAnchor="middle"
            className="fill-slate-500 text-[10px] font-bold"
          >
            {label}
          </text>
        ))}

        {/* Base Fret Indicator */}
        {baseFret > 1 && (
          <text
            x={paddingX - 18}
            y={paddingY + fretSpacing / 2 + 4}
            className="fill-emerald-500 text-[10px] font-black italic"
            textAnchor="middle"
          >
            {baseFret}fr
          </text>
        )}

        {/* Barres */}
        {barres && barres.map((barre, idx) => {
            const startX = paddingX + barre.fromString * stringSpacing;
            const endX = paddingX + barre.toString * stringSpacing;
            const y = getFretY(barre.fret);
            
            if(barre.fret >= baseFret && barre.fret < baseFret + numFrets) {
                return (
                    <rect 
                        key={`barre-${idx}`}
                        x={startX - 7} 
                        y={y - 7} 
                        width={(endX - startX) + 14} 
                        height={14} 
                        rx={7} 
                        fill="#10b981" 
                        className="opacity-90"
                    />
                )
            }
            return null;
        })}

        {/* Finger Dots and Open markers */}
        {frets.map((fret, stringIndex) => {
          const x = paddingX + stringIndex * stringSpacing;
          
          if (isMuted(stringIndex)) {
            return null;
          }
          
          if (isOpen(stringIndex)) {
            return (
              <circle key={`open-${stringIndex}`} cx={x} cy={paddingY - 10} r={4} stroke="#10b981" strokeWidth={1.5} fill="none" />
            );
          }

          if (fret >= baseFret && fret < baseFret + numFrets) {
             const y = getFretY(fret);
             const finger = fingers[stringIndex];
             const isBarred = barres?.some(b => b.fret === fret && stringIndex >= b.fromString && stringIndex <= b.toString);
             
             return (
                <g key={`note-${stringIndex}`}>
                  {!isBarred && <circle cx={x} cy={y} r={9} fill="#10b981" className="drop-shadow-sm" />}
                  {finger > 0 && (
                    <text x={x} y={y + 3.5} textAnchor="middle" className={`${isBarred ? 'fill-emerald-950' : 'fill-white'} text-[9px] font-black`}>
                      {finger}
                    </text>
                  )}
                </g>
             );
          }
          return null;
        })}
      </svg>
    </div>
  );
};

export default Fretboard;